/**
 * Tier-1 deterministic field validation for parent packet forms.
 * Mirrors backend/sessions/packet_field_validation.py — keep logic in sync.
 */

export const GIBBERISH_MESSAGE = 'Please enter a real answer — random letters or keyboard mashing is not accepted';
export const NAME_FORMAT_MESSAGE = 'Use letters only (hyphens and apostrophes OK)';
export const PHONE_FORMAT_MESSAGE = 'Enter a valid 10-digit US phone number';
export const AREA_CODE_FORMAT_MESSAGE = 'Enter a valid 3-digit area code (cannot start with 0 or 1)';
export const PHONE_JUNK_MESSAGE = 'That phone number does not look valid';
export const EMAIL_FORMAT_MESSAGE = 'Enter a valid email address';
export const ZIP_FORMAT_MESSAGE = 'Enter a valid 5-digit ZIP code (optional +4)';
export const DATE_FORMAT_MESSAGE = 'Enter a valid date (YYYY-MM-DD)';
export const DOB_RANGE_MESSAGE = 'Date of birth must be a real past date for a school-age child';

const VOWELS = new Set('aeiouyAEIOUY'.split(''));
const KEYBOARD_RUNS = [
  'asdf', 'asdfg', 'asdfgh', 'qwert', 'qwerty', 'qwer', 'zxcv', 'zxcvb',
  'abcd', 'abcdef', 'abc', 'xyz', '1234', '12345', '123456', 'qazwsx', 'wasd',
];
const ALLERGY_NONE_ALIASES = new Set([
  'none', 'n/a', 'na', 'no', 'no allergies', 'no known allergies', 'none known',
  'nkda', 'no allergy', 'not applicable',
]);
const ALLERGY_FIELDS = new Set(['allergies', 'allergy_food', 'allergy_drug', 'allergy_other']);
const NAME_ALLOWED_RE = /^[a-zA-Z][a-zA-Z\s'.-]*$/;
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const ZIP_RE = /^\d{5}(-\d{4})?$/;
const DATE_INPUT_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const NON_PERSON_NAME_FIELDS = new Set([
  'insurance_name',
  'medication_name',
  'school_district',
]);

const SHORT_NAME_ALLOWLIST = new Set([
  'li', 'wu', 'jo', 'al', 'bo', 'cy', 'di', 'ed', 'iv', 'lo', 'lu', 'mo', 'my', 'nu', 'oz', 'ty', 'vi',
]);

const PROVIDER_BLOCKLIST = new Set([
  'dr', 'md', 'do', 'dds', 'dentist', 'doctor', 'kjl', 'abc', 'xyz', 'xxx', 'asdf', 'qwer', 'nm', 'mb',
]);

const PROVIDER_NAME_FIELDS = new Set([
  'pediatrician_name',
  'dentist_name',
  'preferred_hospital',
  'specialist_name',
]);

const CITY_FIELDS = new Set([
  'home_city',
  'student_city',
]);

const IDENTIFIER_FIELDS = new Set([
  'insurance_id',
  'medicaid_number',
  'other_insurance_policy',
  'insurance_group',
  'medicaid_cin',
]);

const IDENTIFIER_NA_ALIASES = new Set([
  'n/a', 'na', 'none', 'not applicable', 'unknown', 'no insurance', 'no coverage',
]);

const LICENSE_PLATE_RE = /^[A-Za-z0-9][A-Za-z0-9\s-]{1,9}$/;
const CITY_ALLOWED_RE = /^[a-zA-Z][a-zA-Z\s'.-]*$/;

const PERSON_NAME_FIELDS = new Set([
  'person_completing',
  'student_name',
  'parent_name',
  'first_name',
  'last_name',
]);

const PHONE_EXACT_FIELDS = new Set([
  'phone',
  'home_phone',
  'alert_cell_phone',
  'parent_phone',
]);

const DOB_FIELDS = new Set([
  'date_of_birth',
  'child_dob',
  'dob',
  'student_dob',
]);

const ADDRESS_FIELDS = new Set([
  'address',
  'home_address',
  'pickup_address',
  'dropoff_address',
]);

export function stripDigits(v) {
  return String(v ?? '').replace(/\D/g, '');
}

function normalizeToken(text) {
  return String(text ?? '').trim().replace(/\s+/g, ' ');
}

function tokens(text) {
  return normalizeToken(text).split(/[\s-]+/).filter(Boolean);
}

export function hasRepeatedCharRun(text, minRun = 4) {
  if (!text || text.length < minRun) return false;
  let run = 1;
  for (let i = 1; i < text.length; i += 1) {
    if (text[i].toLowerCase() === text[i - 1].toLowerCase()) {
      run += 1;
      if (run >= minRun) return true;
    } else {
      run = 1;
    }
  }
  return false;
}

export function isAllSameChar(text) {
  const compact = String(text ?? '').replace(/\s+/g, '');
  if (compact.length < 2) return false;
  return new Set(compact.toLowerCase().split('')).size === 1;
}

export function hasKeyboardRun(text) {
  const lowered = String(text ?? '').toLowerCase();
  const compact = lowered.replace(/[^a-z0-9]/g, '');
  return KEYBOARD_RUNS.some((run) => compact.includes(run) || lowered.includes(run));
}

export function tokenHasNoVowels(token, minLen = 5) {
  if (!token || token.length < minLen) return false;
  // Dosage / identifier tokens (0.15mg, 5mg, lot numbers) are not words — skip vowel check.
  if (/\d/.test(token)) return false;
  return ![...token].some((c) => VOWELS.has(c));
}

export function hasExcessiveConsonantCluster(token, minCluster = 5) {
  let cluster = 0;
  for (let i = 0; i < token.length; i += 1) {
    const ch = token[i];
    if (/[a-zA-Z]/.test(ch) && !VOWELS.has(ch)) {
      cluster += 1;
      if (cluster >= minCluster) return true;
    } else {
      cluster = 0;
    }
  }
  return false;
}

export function looksLikeGibberishText(text) {
  const normalized = normalizeToken(text);
  if (!normalized) return false;
  if (isAllSameChar(normalized)) return true;
  if (hasRepeatedCharRun(normalized)) return true;
  if (hasKeyboardRun(normalized)) return true;
  for (const token of tokens(normalized)) {
    if (tokenHasNoVowels(token)) return true;
    if (hasExcessiveConsonantCluster(token)) return true;
  }
  if (normalized.length >= 3 && !normalized.includes(' ')) {
    if (![...normalized].some((c) => VOWELS.has(c))) return true;
  }
  return false;
}

export function looksLikeGibberishName(text) {
  const normalized = normalizeToken(text);
  if (!normalized) return false;
  if (isAllSameChar(normalized)) return true;
  if (hasRepeatedCharRun(normalized)) return true;
  if (hasKeyboardRun(normalized)) return true;
  for (const token of tokens(normalized)) {
    if (token.length >= 8 && tokenHasNoVowels(token, 8)) return true;
    if (token.length >= 7 && hasExcessiveConsonantCluster(token, 6)) return true;
  }
  return false;
}

export function validateAllergyValue(value, { required = true, label = 'Allergies' } = {}) {
  const text = normalizeToken(value);
  if (!text) {
    return required ? { code: 'required', message: `${label} is required` } : null;
  }
  const lower = text.toLowerCase().replace(/\.$/, '');
  if (ALLERGY_NONE_ALIASES.has(lower) || lower.startsWith('no known')) return null;
  if (text.length <= 2 && !ALLERGY_NONE_ALIASES.has(lower)) {
    return { code: 'gibberish', message: GIBBERISH_MESSAGE };
  }
  if (looksLikeGibberishText(text)) return { code: 'gibberish', message: GIBBERISH_MESSAGE };
  return null;
}

function isShortNameAllowlisted(text) {
  return SHORT_NAME_ALLOWLIST.has(String(text || '').toLowerCase());
}

export function validateCityValue(value, { required = true, label = 'City' } = {}) {
  const text = normalizeToken(value);
  if (!text) {
    return required ? { code: 'required', message: `${label} is required` } : null;
  }
  if (text.length < 3 && !isShortNameAllowlisted(text)) {
    return { code: 'min_length', message: `${label} must be at least 3 characters` };
  }
  if (!CITY_ALLOWED_RE.test(text)) {
    return { code: 'city_format', message: 'Use letters only (hyphens and apostrophes OK)' };
  }
  if (looksLikeGibberishText(text)) return { code: 'gibberish', message: GIBBERISH_MESSAGE };
  return null;
}

export function validateProviderValue(value, { required = true, label = 'Provider' } = {}) {
  const text = normalizeToken(value);
  if (!text) {
    return required ? { code: 'required', message: `${label} is required` } : null;
  }
  const compact = text.replace(/[^a-zA-Z]/g, '').toLowerCase();
  if (PROVIDER_BLOCKLIST.has(compact) || PROVIDER_BLOCKLIST.has(text.toLowerCase())) {
    return { code: 'gibberish', message: GIBBERISH_MESSAGE };
  }
  const alphaOnly = text.replace(/[^a-zA-Z]/g, '');
  if (alphaOnly && alphaOnly.length < 4 && !isShortNameAllowlisted(alphaOnly)) {
    return { code: 'gibberish', message: GIBBERISH_MESSAGE };
  }
  if (text.length <= 3 && !isShortNameAllowlisted(text)) {
    if (looksLikeGibberishText(text) || tokenHasNoVowels(text, 3)) {
      return { code: 'gibberish', message: GIBBERISH_MESSAGE };
    }
  }
  if (/\d/.test(text)) return { code: 'name_format', message: NAME_FORMAT_MESSAGE };
  if (!NAME_ALLOWED_RE.test(text)) return { code: 'name_format', message: NAME_FORMAT_MESSAGE };
  if (looksLikeGibberishName(text)) return { code: 'gibberish', message: GIBBERISH_MESSAGE };
  return null;
}

export function validateLicensePlateValue(value, { required = true, label = 'License plate' } = {}) {
  const text = String(value ?? '').trim().replace(/\s+/g, ' ');
  if (!text) {
    return required ? { code: 'required', message: `${label} is required` } : null;
  }
  if (text.length < 2 || text.length > 10) {
    return { code: 'plate_format', message: `${label} must be 2–10 characters` };
  }
  if (!LICENSE_PLATE_RE.test(text)) {
    return { code: 'plate_format', message: `${label} must use letters and numbers only` };
  }
  const compact = text.replace(/[\s-]/g, '');
  if (isAllSameChar(compact)) return { code: 'gibberish', message: GIBBERISH_MESSAGE };
  const alpha = compact.replace(/[^a-zA-Z]/g, '');
  if (alpha && hasKeyboardRun(alpha)) return { code: 'gibberish', message: GIBBERISH_MESSAGE };
  if (!/\d/.test(compact) && looksLikeGibberishText(compact)) {
    return { code: 'gibberish', message: GIBBERISH_MESSAGE };
  }
  return null;
}

export function validateIdentifierValue(value, { required = true, label = 'ID number' } = {}) {
  const text = String(value ?? '').trim();
  if (!text) {
    return required ? { code: 'required', message: `${label} is required` } : null;
  }
  const normalized = text.toLowerCase().replace(/\./g, '');
  if (IDENTIFIER_NA_ALIASES.has(normalized)) {
    return null;
  }
  const compact = text.replace(/[\s-]/g, '');
  if (compact.length < 3 || compact.length > 25) {
    return { code: 'identifier_format', message: `${label} must be 3–25 letters or numbers (or enter N/A if not applicable)` };
  }
  if (!/^[A-Za-z0-9\s\-/#.]+$/.test(text)) {
    return { code: 'identifier_format', message: `${label} must use letters, numbers, and common punctuation only` };
  }
  if (!/\d/.test(compact) && looksLikeGibberishText(compact)) {
    return { code: 'gibberish', message: GIBBERISH_MESSAGE };
  }
  return null;
}

export function validateOptionalTextValue(value, { label = 'This field' } = {}) {
  const text = normalizeToken(value);
  if (!text) return null;
  if (looksLikeGibberishText(text)) return { code: 'gibberish', message: GIBBERISH_MESSAGE };
  return null;
}

export function inferFieldKind(field) {
  if (field.endsWith('_plate')) return 'license_plate';
  if (DOB_FIELDS.has(field)) return 'dob';
  if (PHONE_EXACT_FIELDS.has(field) || field.endsWith('_phone') || field.endsWith('_cell')) return 'phone';
  if (field.endsWith('_area_code') || field === 'area_code') return 'area_code';
  if (field.endsWith('_cell_phone') || field.endsWith('_home_phone')) return 'phone';
  if (field === 'email' || field.endsWith('_email')) return 'email';
  if (field === 'zip' || field.endsWith('_zip')) return 'zip';
  if (PROVIDER_NAME_FIELDS.has(field)) return 'provider';
  if (CITY_FIELDS.has(field) || field.endsWith('_city')) return 'city';
  if (IDENTIFIER_FIELDS.has(field)) return 'identifier';
  if (PERSON_NAME_FIELDS.has(field) || (field.endsWith('_name') && !NON_PERSON_NAME_FIELDS.has(field))) {
    return 'name';
  }
  if (ADDRESS_FIELDS.has(field) || field.includes('address')) return 'address';
  if (ALLERGY_FIELDS.has(field)) return 'allergy';
  return 'text';
}

export function formatUsPhone(digits) {
  const d = stripDigits(digits);
  if (d.length !== 10) return String(digits ?? '').trim();
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
}

/** Live phone mask while typing — partial `(555) 123-4567` as digits arrive. */
export function formatPhoneInput(value) {
  const digits = stripDigits(value).slice(0, 10);
  if (!digits) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function isRepeatedDigitPhone(digits) {
  return digits.length === 10 && new Set(digits.split('')).size === 1;
}

function parseIsoDate(value) {
  const m = DATE_INPUT_RE.exec(String(value ?? '').trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return dt;
}

export function validateNameValue(value, { required = true } = {}) {
  const text = normalizeToken(value);
  if (!text) {
    return required ? { code: 'required', message: 'This field is required' } : null;
  }
  if (text.length < 2) return { code: 'name_format', message: 'Enter at least 2 characters' };
  if (text.length === 2 && !isShortNameAllowlisted(text)) {
    for (const token of tokens(text)) {
      if (token.length === 2 && !isShortNameAllowlisted(token)) {
        if (tokenHasNoVowels(token, 2) || looksLikeGibberishText(token)) {
          return { code: 'gibberish', message: GIBBERISH_MESSAGE };
        }
      }
    }
  }
  if (/\d/.test(text)) return { code: 'name_format', message: NAME_FORMAT_MESSAGE };
  if (!NAME_ALLOWED_RE.test(text)) return { code: 'name_format', message: NAME_FORMAT_MESSAGE };
  if (looksLikeGibberishName(text)) return { code: 'gibberish', message: GIBBERISH_MESSAGE };
  return null;
}

export function validateRequiredTextValue(value, { required = true, minLength = 2, label = 'This field' } = {}) {
  const text = normalizeToken(value);
  if (!text) {
    return required ? { code: 'required', message: `${label} is required` } : null;
  }
  if (text.length < minLength) {
    return { code: 'min_length', message: `${label} must be at least ${minLength} characters` };
  }
  if (looksLikeGibberishText(text)) return { code: 'gibberish', message: GIBBERISH_MESSAGE };
  return null;
}

export function validateAddressValue(value, { required = true, label = 'Address' } = {}) {
  const text = normalizeToken(value);
  if (!text) {
    return required ? { code: 'required', message: `${label} is required` } : null;
  }
  if (text.length < 5) return { code: 'min_length', message: `${label} must be at least 5 characters` };
  if (looksLikeGibberishText(text)) return { code: 'gibberish', message: GIBBERISH_MESSAGE };
  return null;
}

export function validatePhoneValue(value, { required = false, label = 'Phone' } = {}) {
  const text = String(value ?? '').trim();
  if (!text) {
    return required ? { code: 'required', message: `${label} is required` } : null;
  }
  const digits = stripDigits(text);
  if (/[a-zA-Z]/.test(text)) return { code: 'phone_format', message: PHONE_FORMAT_MESSAGE };
  if (digits.length !== 10) return { code: 'phone_format', message: `${label} must be 10 digits` };
  if (isRepeatedDigitPhone(digits)) return { code: 'phone_junk', message: PHONE_JUNK_MESSAGE };
  return null;
}

export function validateAreaCodeValue(value, { required = false, label = 'Area code' } = {}) {
  const text = String(value ?? '').trim();
  if (!text) {
    return required ? { code: 'required', message: `${label} is required` } : null;
  }
  const digits = stripDigits(text);
  if (/[a-zA-Z]/.test(text) || digits.length !== 3) {
    return { code: 'phone_format', message: `${label} must be 3 digits` };
  }
  if (digits[0] === '0' || digits[0] === '1') {
    return { code: 'phone_format', message: AREA_CODE_FORMAT_MESSAGE };
  }
  return null;
}

export function validateEmailValue(value, { required = true, label = 'Email' } = {}) {
  const text = String(value ?? '').trim();
  if (!text) {
    return required ? { code: 'required', message: `${label} is required` } : null;
  }
  if (!EMAIL_RE.test(text)) return { code: 'email_format', message: EMAIL_FORMAT_MESSAGE };
  if (looksLikeGibberishText(text.split('@')[0])) {
    return { code: 'gibberish', message: GIBBERISH_MESSAGE };
  }
  return null;
}

export function validateZipValue(value, { required = true, label = 'ZIP code' } = {}) {
  const text = String(value ?? '').trim();
  if (!text) {
    return required ? { code: 'required', message: `${label} is required` } : null;
  }
  if (!ZIP_RE.test(text)) return { code: 'zip_format', message: ZIP_FORMAT_MESSAGE };
  return null;
}

export function validateDobValue(value, { required = true, label = 'Date of birth' } = {}) {
  const text = String(value ?? '').trim();
  if (!text) {
    return required ? { code: 'required', message: `${label} is required` } : null;
  }
  const parsed = parseIsoDate(text);
  if (!parsed) return { code: 'date_format', message: DATE_FORMAT_MESSAGE };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (parsed >= today) return { code: 'dob_range', message: DOB_RANGE_MESSAGE };
  let ageYears = today.getFullYear() - parsed.getFullYear();
  const md = today.getMonth() - parsed.getMonth();
  if (md < 0 || (md === 0 && today.getDate() < parsed.getDate())) ageYears -= 1;
  if (ageYears > 25 || parsed.getFullYear() < 1990) return { code: 'dob_range', message: DOB_RANGE_MESSAGE };
  return null;
}

export function validateTypedField(field, value, label, { required = false, kind = null } = {}) {
  const fieldKind = kind || inferFieldKind(field);
  let err = null;
  if (fieldKind === 'phone') err = validatePhoneValue(value, { required, label });
  else if (fieldKind === 'area_code') err = validateAreaCodeValue(value, { required, label });
  else if (fieldKind === 'email') err = validateEmailValue(value, { required, label });
  else if (fieldKind === 'zip') err = validateZipValue(value, { required, label });
  else if (fieldKind === 'dob') err = validateDobValue(value, { required, label });
  else if (fieldKind === 'name') err = validateNameValue(value, { required });
  else if (fieldKind === 'address') err = validateAddressValue(value, { required, label });
  else if (fieldKind === 'allergy') err = validateAllergyValue(value, { required, label });
  else if (fieldKind === 'city') err = validateCityValue(value, { required, label });
  else if (fieldKind === 'provider') err = validateProviderValue(value, { required, label });
  else if (fieldKind === 'license_plate') err = validateLicensePlateValue(value, { required, label });
  else if (fieldKind === 'identifier') err = validateIdentifierValue(value, { required, label });
  else if (fieldKind === 'text') {
    if (required) {
      const minLen = field === 'sex' ? 1 : 2;
      err = validateRequiredTextValue(value, { required, minLength: minLen, label });
    } else {
      err = validateOptionalTextValue(value, { label });
    }
  }
  if (!err) return null;
  if (fieldKind === 'name' && err.code === 'required') {
    return { ...err, message: `${label} is required` };
  }
  return err;
}

export function normalizeFieldValue(field, value) {
  const kind = inferFieldKind(field);
  if (kind === 'phone') {
    const digits = stripDigits(value);
    if (digits.length === 10 && !isRepeatedDigitPhone(digits)) return formatUsPhone(digits);
  }
  if (kind === 'email' && value) return String(value).trim().toLowerCase();
  if ((kind === 'name' || kind === 'text' || kind === 'address') && value) return normalizeToken(value);
  return value;
}
